import apiClient from './client';
import type { Question, QuestionList, QuestionsParams, Stats, Topic, Subtopic } from '../types';
import type { DailyUsage } from '../types/questions';

/** Fetch paginated questions with optional filters. */
export async function fetchQuestions(
  params?: QuestionsParams,
  certification?: string,
): Promise<QuestionList> {
  const { data } = await apiClient.get<QuestionList>('/api/questions', {
    params: { ...params, ...(certification ? { certification } : {}) },
  });
  return data;
}

/** Fetch a single question by ID. */
export async function fetchQuestion(id: number): Promise<Question> {
  const { data } = await apiClient.get<Question>(`/api/questions/${id}`);
  return data;
}

/** Fetch N random questions (used for exam mode). */
export async function fetchRandomQuestions(
  count: number,
  certification?: string,
): Promise<Question[]> {
  const { data } = await apiClient.get<Question[]>('/api/questions/random', {
    params: { count, ...(certification ? { certification } : {}) },
  });
  return data;
}

/** Fetch global statistics. */
export async function fetchStats(certification?: string): Promise<Stats> {
  const { data } = await apiClient.get<Stats>('/api/questions/stats', {
    params: certification ? { certification } : undefined,
  });
  return data;
}

/** Fetch distinct topics with question counts. */
export async function fetchTopics(certification?: string): Promise<Topic[]> {
  const { data } = await apiClient.get<Topic[]>('/api/topics', {
    params: certification ? { certification } : undefined,
  });
  return data;
}

/** Fetch the subtopic catalog (with counts), optionally scoped to a domain. */
export async function fetchSubtopics(
  certification?: string,
  topic?: string,
): Promise<Subtopic[]> {
  const { data } = await apiClient.get<Subtopic[]>('/api/subtopics', {
    params: {
      ...(certification ? { certification } : {}),
      ...(topic ? { topic } : {}),
    },
  });
  return data;
}

/** Fetch daily usage stats for free-plan users. */
export async function fetchDailyUsage(): Promise<DailyUsage> {
  const { data } = await apiClient.get<DailyUsage>('/api/questions/daily-usage');
  return data;
}
